import React from "react";
import styled from "styled-components";
import { useForceHTMLInputCheckedAttribute } from "../hooks/useForceHTMLInputCheckedAttribute";
import { useIsFocused } from "../hooks/useIsFocused";
import { Icon } from "../Icon";
import { forkedRef } from "../utils/forkedRef";
import { isObject } from "../utils/isObject";
import { FatButton } from "./FatButton";

const INPUT_SIZE = "16px";
const ICON_SIZE = "24px";

type GridTemplateProps = {
  $hasSubtitle: boolean;
  $hasRightSide: boolean;
  $hasIcon: boolean;
};

type ChildrenProps = {
  id?: string;
  "aria-labelledby"?: string;
  value: string;
  checked: boolean;
  disabled: boolean;
  ref: React.Ref<HTMLInputElement>;
};

type Props = {
  id?: string;
  checked: boolean;
  horizontal: boolean;
  disabled: boolean;
  invalid: boolean;
  value: string | null;
  title: React.ReactNode;

  subtitle?: React.ReactNode;
  rightSide?: FatButtonInputRightSideProp;
  extension?: React.ReactNode;
  icon?: string | null;

  flatTopLeft?: boolean;
  flatTopRight?: boolean;
  flatBottomLeft?: boolean;
  flatBottomRight?: boolean;

  children: React.FC<ChildrenProps>;
  compact?: boolean;
};

type RightSideSplit = {
  upper: React.ReactNode | RightSideNodeFC;
  lower?: React.ReactNode | RightSideNodeFC;
  hoist?: boolean;
};

type RideSideNodeProps = { disabled: boolean; checked: boolean };

type RightSideNodeFC = React.FC<RideSideNodeProps>;

export type FatButtonInputRightSideProp = React.ReactNode | RightSideNodeFC | RightSideSplit;

const isRightSideSplit = (rightSide: FatButtonInputRightSideProp): rightSide is RightSideSplit => {
  return isObject(rightSide) && "upper" in rightSide;
};

const renderRightSideNode = (
  node: React.ReactNode | RightSideNodeFC,
  wrapString: (node: string, param: RideSideNodeProps) => JSX.Element,
  param: RideSideNodeProps
) => {
  switch (typeof node) {
    case "string":
      return wrapString(node, param);
    case "function": {
      const Component = node;
      return <Component {...param} />;
    }
    default:
      return node ?? null;
  }
};

const wrapTitleString = (str: string) => <FatButton.TitleText children={str} />;

const wrapSubtitleString = (str: string, param: RideSideNodeProps) => (
  <FatButton.SubtitleText $disabled={param.disabled} $highlight={param.checked} children={str} />
);

/**
 * This is a function that accepts a `FatButtonInputRightSideProp`, which can be several things,
 * and transforms it into two `React.ReactNode`s. The `inner` node is the one that is rendered
 * inside the main grid, while the `outer` node is rendered outside the main grid. The `outer`
 * node is hoisted to the top level of the component. This is necessary sometimes when trying to
 * get the right tab-navigation order. If the element is inside the grid, then it will end up
 * selecting both the input and the right side element, which is not what we want when the right
 * side element is also an input or button.
 */
const rightSideNormalizer = (rightSide: FatButtonInputRightSideProp, param: RideSideNodeProps) => {
  let upper: React.ReactNode;
  let lower: React.ReactNode;
  let hoistRightSide: boolean;

  if (isRightSideSplit(rightSide)) {
    upper = renderRightSideNode(rightSide.upper, wrapTitleString, param);
    lower = renderRightSideNode(rightSide.lower, wrapSubtitleString, param);
    hoistRightSide = rightSide.hoist ?? false;
  } else {
    upper = renderRightSideNode(rightSide, wrapTitleString, param);
    lower = null;
    hoistRightSide = false;
  }

  let node: React.ReactNode;

  if (upper === null && lower === null) {
    node = null;
  } else if (lower === null) {
    node = upper;
  } else if (upper === null) {
    node = lower;
  } else {
    node = (
      <>
        <div>{upper}</div>
        <div>{lower}</div>
      </>
    );
  }

  return hoistRightSide ? { inner: null, outer: node } : { inner: node, outer: null };
};

const InputWrapper = styled.div`
  all: unset;

  box-sizing: border-box;

  grid-area: input;
  align-self: center;

  width: ${INPUT_SIZE};
  height: ${INPUT_SIZE};
`;

const calculateGridTemplate = (props: GridTemplateProps) => {
  /**
   * Calculates the grid for the label container depending on whether
   * other sub-components appear in the component. For example, if there
   * is an icon, we have to create an additional column for the icon.
   */

  const gridTemplateColumns = [INPUT_SIZE];

  const gridTemplateAreas = {
    row1: [] as string[],
    row2: [] as string[],
    row3: [] as string[],
    row4: [] as string[],
  };

  if (props.$hasSubtitle && !props.$hasIcon) {
    gridTemplateAreas.row1.push(".");
    gridTemplateAreas.row2.push("input");
    gridTemplateAreas.row3.push(".");
    gridTemplateAreas.row4.push(".");
  } else {
    gridTemplateAreas.row1.push("input");
    gridTemplateAreas.row2.push("input");
    gridTemplateAreas.row3.push("input");
    gridTemplateAreas.row4.push("input");
  }

  if (props.$hasIcon) {
    gridTemplateAreas.row1.push("icon");
    gridTemplateAreas.row2.push("icon");
    gridTemplateAreas.row3.push("icon");
    gridTemplateAreas.row4.push("icon");
    gridTemplateColumns.push(ICON_SIZE);
  }

  if (props.$hasSubtitle) {
    gridTemplateAreas.row1.push(".");
    gridTemplateAreas.row2.push("title");
    gridTemplateAreas.row3.push("subtitle");
    gridTemplateAreas.row4.push(".");
    gridTemplateColumns.push("1fr");
  } else {
    gridTemplateAreas.row1.push("title");
    gridTemplateAreas.row2.push("title");
    gridTemplateAreas.row3.push("title");
    gridTemplateAreas.row4.push("title");
    gridTemplateColumns.push("1fr");
  }

  if (props.$hasRightSide) {
    gridTemplateAreas.row1.push("rhs");
    gridTemplateAreas.row2.push("rhs");
    gridTemplateAreas.row3.push("rhs");
    gridTemplateAreas.row4.push("rhs");
    gridTemplateColumns.push("auto");
  }

  return {
    gridTemplateRows: "auto auto auto auto",
    gridTemplateColumns: gridTemplateColumns.join(" "),
    gridTemplateAreas: `
      '${gridTemplateAreas.row1.join(" ")}'
      '${gridTemplateAreas.row2.join(" ")}'
      '${gridTemplateAreas.row3.join(" ")}'
      '${gridTemplateAreas.row4.join(" ")}'
    `,
    columnGap: "12px",
    rowGap: "0px",
  };
};

export const FatButtonInputWrapper = React.forwardRef<HTMLInputElement, Props>((props, forwardedRef) => {
  const {
    id,
    value,
    title,
    icon = null,
    subtitle = null,
    extension = null,
    horizontal,
    disabled,
    invalid,
    checked,
    children,
    flatTopLeft = false,
    flatTopRight = false,
    flatBottomLeft = checked && !!extension,
    flatBottomRight = checked && !!extension,
    compact = false,
  } = props;

  const rightSide = rightSideNormalizer(props.rightSide, { disabled, checked });
  let labelledBy: undefined | string;

  if (id !== undefined) {
    labelledBy = `${id}-title`;
  }

  const labelRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const labelIsFocused = useIsFocused(labelRef);
  const inputIsFocused = useIsFocused(inputRef);
  const isFocused = labelIsFocused || inputIsFocused;

  const gridTemplate = React.useMemo(() => {
    return calculateGridTemplate({
      $hasSubtitle: subtitle !== null,
      $hasIcon: icon !== null,
      $hasRightSide: rightSide.inner !== null,
    });
  }, [subtitle !== null, icon !== null, rightSide.inner !== null]);

  useForceHTMLInputCheckedAttribute(inputRef, checked);

  const input = children({
    id,
    "aria-labelledby": labelledBy,
    value: (value === null ? undefined : value) as string,
    checked,
    disabled,
    ref: forkedRef(forwardedRef, inputRef),
  });

  return (
    <div>
      <FatButton.Container
        ref={labelRef}
        $highlight={checked}
        $disabled={disabled}
        $invalid={invalid}
        $focused={isFocused}
        $horizontal={horizontal}
        $flatTopLeft={flatTopLeft}
        $flatTopRight={flatTopRight}
        $flatBottomLeft={flatBottomLeft}
        $flatBottomRight={flatBottomRight}
        $compact={compact}
        $hideOverflow={false}
      >
        <FatButton.Grid $compact={false} htmlFor={id} $gridTemplate={gridTemplate}>
          <InputWrapper>{input}</InputWrapper>

          {icon !== null && (
            <FatButton.IconWrapper $faded={disabled}>
              <Icon aria-hidden icon={icon} xlarge />
            </FatButton.IconWrapper>
          )}

          <FatButton.TitleWrapper $hideOverflow={false} $faded={disabled} id={labelledBy}>
            <FatButton.TitleText>{title}</FatButton.TitleText>
          </FatButton.TitleWrapper>

          {subtitle !== null && (
            /* This subtitle wrapper is intentionally never faded when the value is a string because we want the user to be able to see WHY IT IS DISABLED. */
            <FatButton.SubtitleWrapper $hideOverflow={false} $faded={typeof subtitle === "string" ? false : disabled}>
              <FatButton.SubtitleText $disabled={disabled} $highlight={checked}>
                {subtitle}
              </FatButton.SubtitleText>
            </FatButton.SubtitleWrapper>
          )}
          {rightSide.inner !== null && <FatButton.RightSideWrapper $faded={disabled}>{rightSide.inner}</FatButton.RightSideWrapper>}
        </FatButton.Grid>
        {rightSide.outer !== null && <FatButton.RightSideWrapper $faded={disabled}>{rightSide.outer}</FatButton.RightSideWrapper>}
      </FatButton.Container>
      {checked && extension && <FatButton.Extension $faded={disabled}>{extension}</FatButton.Extension>}
    </div>
  );
});

FatButtonInputWrapper.displayName = "FatButtonInputWrapper";
